import React, { useState, useEffect } from "react";
import toast from 'react-hot-toast';
// import { useSelector } from "react-redux";
// import type { RootState } from "../../../store";
import {
  UserGroupIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ArrowDownIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import Button from "../../../components/ui/Button";
import Card, { CardContent } from "../../../components/ui/Card";
import Modal, { ModalContent, ModalFooter } from "../../../components/ui/Modal";
import FormInput from "../../../components/ui/FormInput";
import Textarea from "../../../components/ui/Textarea";
import { patronCrmService } from "../../../services/patronCrmService";

interface Patron {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    avatar?: string;
  };
  membership: {
    id: string;
    tier: {
      id: string;
      name: string;
      amount: number;
      currency: string;
      benefits: string[];
    };
    status: "ACTIVE" | "CANCELLED" | "PAUSED" | "EXPIRED";
    amount: number;
    currency: string;
    billingCycle: "MONTHLY" | "YEARLY";
    currentPeriodEnd: string;
    pledgeDate: string;
    lastChargeDate?: string;
    lifetimeMember: boolean;
    patronSince: string;
  };
  totalSpent: number;
  currentStreak: number;
  longestStreak: number;
  riskLevel: "LOW" | "MEDIUM" | "HIGH";
  lastActiveAt: string;
  engagementScore: number;
  notes: string;
}

interface AnalyticsOverview {
  totalPatrons: number;
  activePatrons: number;
  newPatronsThisMonth: number;
  churnedPatronsThisMonth: number;
  monthlyRecurringRevenue: number;
  averageRevenuePerPatron: number;
  churnRate: number;
  lifetimeValue: number;
}

const PatronsPage: React.FC = () => {
  // const { user } = useSelector((state: RootState) => state.auth);
  const [patrons, setPatrons] = useState<Patron[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTier, setSelectedTier] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPatron, setSelectedPatron] = useState<Patron | null>(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [patronNote, setPatronNote] = useState("");
  const [engagementFilter, setEngagementFilter] = useState<string>("");

  // CRM state
  const [patronNotes, setPatronNotes] = useState<import("../../../services/patronCrmService").PatronNote[]>([]);
  const [patronTags, setPatronTags] = useState<import("../../../services/patronCrmService").PatronTag[]>([]);
  const [allTags, setAllTags] = useState<import("../../../services/patronCrmService").PatronTag[]>([]);
  const [assignTagId, setAssignTagId] = useState<string>("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState<string>("");
  const [isSavingNote, setIsSavingNote] = useState(false);

  useEffect(() => {
    const loadCrm = async () => {
      if (!showNoteModal || !selectedPatron) return;
      try {
        const [notes, tags, available] = await Promise.all([
          patronCrmService.listNotes(selectedPatron.user.id),
          patronCrmService.getPatronTags(selectedPatron.user.id),
          patronCrmService.listTags(),
        ]);
        setPatronNotes(notes);
        setPatronTags(tags);
        setAllTags(available);
      } catch (e) {
        console.error("Failed to load patron CRM data", e);
      }
    };
    loadCrm();
  }, [showNoteModal, selectedPatron]);

  useEffect(() => {
    loadPatrons();
  }, [searchTerm, selectedTier, selectedStatus]);

  const loadPatrons = async () => {
    setLoading(true);
    try {
      // Simulate API call with filtering
      setTimeout(() => {
        const mockPatrons: Patron[] = [
          {
            id: "1",
            user: {
              id: "1",
              firstName: "Emily",
              lastName: "Rodriguez",
              username: "emilyr",
              email: "emily.r@example.com",
              avatar: undefined,
            },
            membership: {
              id: "1",
              tier: {
                id: "3",
                name: "Platinum VIP",
                amount: 100,
                currency: "USD",
                benefits: [
                  "Everything in Gold",
                  "1-on-1 video call",
                  "Name in credits",
                  "Exclusive merchandise",
                ],
              },
              status: "ACTIVE",
              amount: 100,
              currency: "USD",
              billingCycle: "MONTHLY",
              currentPeriodEnd: "2024-02-15T00:00:00Z",
              pledgeDate: "2023-01-15T00:00:00Z",
              lastChargeDate: "2024-01-15T00:00:00Z",
              lifetimeMember: true,
              patronSince: "2023-01-15T00:00:00Z",
            },
            totalSpent: 894.5,
            currentStreak: 12,
            longestStreak: 18,
            riskLevel: "LOW",
            lastActiveAt: "2024-01-15T14:30:00Z",
            engagementScore: 95,
            notes:
              "Very engaged patron, frequently comments and shares content.",
          },
          {
            id: "2",
            user: {
              id: "2",
              firstName: "David",
              lastName: "Kim",
              username: "davidk",
              email: "david.k@example.com",
              avatar: undefined,
            },
            membership: {
              id: "2",
              tier: {
                id: "2",
                name: "Gold Patron",
                amount: 25,
                currency: "USD",
                benefits: [
                  "Everything in Bronze",
                  "Early access",
                  "Exclusive videos",
                  "Patron-only Discord",
                ],
              },
              status: "ACTIVE",
              amount: 25,
              currency: "USD",
              billingCycle: "MONTHLY",
              currentPeriodEnd: "2024-02-15T00:00:00Z",
              pledgeDate: "2023-03-15T00:00:00Z",
              lastChargeDate: "2024-01-15T00:00:00Z",
              lifetimeMember: false,
              patronSince: "2023-03-15T00:00:00Z",
            },
            totalSpent: 654.0,
            currentStreak: 10,
            longestStreak: 15,
            riskLevel: "LOW",
            lastActiveAt: "2024-01-14T16:45:00Z",
            engagementScore: 88,
            notes: "",
          },
          {
            id: "3",
            user: {
              id: "3",
              firstName: "Lisa",
              lastName: "Thompson",
              username: "lisat",
              email: "lisa.t@example.com",
              avatar: undefined,
            },
            membership: {
              id: "3",
              tier: {
                id: "1",
                name: "Bronze Supporter",
                amount: 5,
                currency: "USD",
                benefits: [
                  "Access to all posts",
                  "Community Discord",
                  "Monthly Q&A",
                ],
              },
              status: "PAUSED",
              amount: 5,
              currency: "USD",
              billingCycle: "MONTHLY",
              currentPeriodEnd: "2024-02-15T00:00:00Z",
              pledgeDate: "2023-06-15T00:00:00Z",
              lastChargeDate: "2023-12-15T00:00:00Z",
              lifetimeMember: false,
              patronSince: "2023-06-15T00:00:00Z",
            },
            totalSpent: 432.0,
            currentStreak: 0,
            longestStreak: 8,
            riskLevel: "MEDIUM",
            lastActiveAt: "2023-12-14T09:20:00Z",
            engagementScore: 65,
            notes: "Patron since June, paused membership in December.",
          },
          {
            id: "4",
            user: {
              id: "4",
              firstName: "Michael",
              lastName: "Brown",
              username: "michaelb",
              email: "michael.b@example.com",
              avatar: undefined,
            },
            membership: {
              id: "4",
              tier: {
                id: "2",
                name: "Gold Patron",
                amount: 25,
                currency: "USD",
                benefits: [
                  "Everything in Bronze",
                  "Early access",
                  "Exclusive videos",
                  "Patron-only Discord",
                ],
              },
              status: "CANCELLED",
              amount: 25,
              currency: "USD",
              billingCycle: "MONTHLY",
              currentPeriodEnd: "2024-01-15T00:00:00Z",
              pledgeDate: "2023-08-15T00:00:00Z",
              lastChargeDate: "2023-12-15T00:00:00Z",
              lifetimeMember: false,
              patronSince: "2023-08-15T00:00:00Z",
            },
            totalSpent: 200.0,
            currentStreak: 0,
            longestStreak: 5,
            riskLevel: "HIGH",
            lastActiveAt: "2023-12-10T14:15:00Z",
            engagementScore: 42,
            notes:
              "Cancelled membership after 4 months, was active in Discord.",
          },
        ];

        // Apply filters
        let filteredPatrons = mockPatrons;

        if (searchTerm) {
          filteredPatrons = filteredPatrons.filter((patron) =>
            `${patron.user.firstName} ${patron.user.lastName} ${patron.user.username} ${patron.user.email}`
              .toLowerCase()
              .includes(searchTerm.toLowerCase()),
          );
        }

        if (selectedTier !== "all") {
          filteredPatrons = filteredPatrons.filter(
            (patron) => patron.membership.tier.id === selectedTier,
          );
        }

        if (selectedStatus !== "all") {
          filteredPatrons = filteredPatrons.filter(
            (patron) => patron.membership.status === selectedStatus,
          );
        }

        setPatrons(filteredPatrons);

        // Mock analytics data
        setAnalytics({
          totalPatrons: mockPatrons.length,
          activePatrons: mockPatrons.filter(
            (p) => p.membership.status === "ACTIVE",
          ).length,
          newPatronsThisMonth: 12,
          churnedPatronsThisMonth: 8,
          monthlyRecurringRevenue: 8924.5,
          averageRevenuePerPatron: 26.8,
          churnRate: 3.2,
          lifetimeValue: 245.6,
        });

        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Failed to load patrons:", error);
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedPatron || !patronNote.trim()) return;

    setIsSavingNote(true);
    try {
      const newNote = await patronCrmService.createNote({ patronId: selectedPatron.user.id, content: patronNote.trim(), pinned: false });
      setPatronNotes((prev) => [newNote, ...prev]);
      setPatronNote("");
      toast.success('Note added');
    } catch (error) {
      console.error("Failed to add note:", error);
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleTogglePin = async (noteId: string, pinned: boolean) => {
    try {
      const updated = await patronCrmService.updateNote(noteId, { pinned: !pinned });
      setPatronNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
      toast.success(updated.pinned ? 'Note pinned' : 'Note unpinned');
    } catch (error) {
      console.error("Failed to toggle pin:", error);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await patronCrmService.deleteNote(noteId);
      setPatronNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      console.error("Failed to delete note:", error);
    }
  };

  const startEditNote = (noteId: string, content: string) => {
    setEditingNoteId(noteId);
    setEditingContent(content);
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingContent("");
  };

  const saveEditNote = async () => {
    if (!editingNoteId) return;
    try {
      const updated = await patronCrmService.updateNote(editingNoteId, { content: editingContent });
      setPatronNotes((prev) => prev.map((n) => (n.id === editingNoteId ? updated : n)));
      setEditingNoteId(null);
      setEditingContent("");
    } catch (error) {
      console.error("Failed to save note:", error);
    }
  };

  const handleAssignTag = async () => {
    if (!selectedPatron || !assignTagId) return;
    try {
      await patronCrmService.assignTag(assignTagId, selectedPatron.user.id);
      const assigned = allTags.find((t) => t.id === assignTagId);
      if (assigned) setPatronTags((prev) => [...prev, assigned]);
      setAssignTagId("");
      toast.success('Tag assigned');
    } catch (error) {
      console.error("Failed to assign tag:", error);
    }
  };

  const handleUnassignTag = async (tagId: string) => {
    if (!selectedPatron) return;
    try {
      await patronCrmService.unassignTag(tagId, selectedPatron.user.id);
      setPatronTags((prev) => prev.filter((t) => t.id !== tagId));
      toast.success('Tag removed');
    } catch (error) {
      console.error("Failed to unassign tag:", error);
    }
  };

  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case "LOW":
        return "text-green-600 bg-green-100";
      case "MEDIUM":
        return "text-yellow-600 bg-yellow-100";
      case "HIGH":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "text-green-600 bg-green-100";
      case "PAUSED":
        return "text-yellow-600 bg-yellow-100";
      case "CANCELLED":
        return "text-red-600 bg-red-100";
      case "EXPIRED":
        return "text-gray-600 bg-gray-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return <CheckCircleIcon className="w-4 h-4" />;
      case "PAUSED":
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case "CANCELLED":
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return <div className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const tiers = Array.from(new Set(patrons.map((p) => p.membership.tier))).map(
    (tier) => ({ id: tier.id, name: tier.name }),
  );

  const statuses = ["ACTIVE", "PAUSED", "CANCELLED", "EXPIRED"];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Patrons</h1>
          <p className="text-gray-600 mt-2">
            Manage your supporters and track their engagement
          </p>
        </div>
        <Button
          leftIcon={<EnvelopeIcon className="w-5 h-5" />}
          variant="outline"
        >
          Email All Patrons
        </Button>
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Patrons
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {analytics.totalPatrons.toLocaleString()}
                  </p>
                  <div className="flex items-center mt-2 text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        analytics.newPatronsThisMonth > 0
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      +{analytics.newPatronsThisMonth} this month
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <UserGroupIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Active Patrons
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {analytics.activePatrons.toLocaleString()}
                  </p>
                  <div className="flex items-center mt-2 text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        analytics.churnedPatronsThisMonth === 0
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {Math.round(
                        (analytics.activePatrons / analytics.totalPatrons) *
                          100,
                      )}
                      % active
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <ShieldCheckIcon className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Monthly Revenue
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatCurrency(analytics.monthlyRecurringRevenue)}
                  </p>
                  <div className="flex items-center mt-2 text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        analytics.churnRate < 5
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {formatCurrency(analytics.averageRevenuePerPatron)}{" "}
                      avg/patron
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <CurrencyDollarIcon className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Churn Rate
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {analytics.churnRate}%
                  </p>
                  <div className="flex items-center mt-2 text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        analytics.churnRate < 5
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {analytics.churnedPatronsThisMonth} churned this month
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <ArrowDownIcon className="w-6 h-6" style={{ color: 'var(--patreon-orange)' }} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search patrons by name, username, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
                style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
                onFocus={(e) => { e.target.style.borderColor = 'var(--patreon-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
              />
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
              style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
              onFocus={(e) => { e.target.style.borderColor = 'var(--patreon-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
            >
              <option value="all">All Tiers</option>
              {tiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
              style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
              onFocus={(e) => { e.target.style.borderColor = 'var(--patreon-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
            >
              <option value="all">All Status</option>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              leftIcon={<AdjustmentsHorizontalIcon className="w-5 h-5" />}
            >
              Advanced Filters
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormInput
                label="Join Date Range"
                type="date"
                placeholder="Filter by join date"
              />
              <FormInput
                label="Total Spent Range"
                type="text"
                placeholder="Filter by amount spent"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Engagement Score</label>
                <select
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[color:var(--patreon-orange)] focus:ring-[color:var(--patreon-orange)]"
                  value={engagementFilter}
                  onChange={(e) => setEngagementFilter(e.target.value)}
                
                >
                  <option value="">All</option>
                  <option value="high">High (80-100)</option>
                  <option value="medium">Medium (50-79)</option>
                  <option value="low">Low (0-49)</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Patrons List */}
      <Card>
        <CardContent className="p-0">
          {patrons.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No patrons found
              </h3>
              <p className="text-gray-600">
                {searchTerm ||
                selectedTier !== "all" ||
                selectedStatus !== "all"
                  ? "Try adjusting your search or filters."
                  : "No patrons yet. Share your page to start building your community!"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patron
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Spent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Engagement
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Risk Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {patrons.map((patron) => (
                    <tr key={patron.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {patron.user.firstName.charAt(0)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {patron.user.firstName} {patron.user.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              @{patron.user.username}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">
                            {patron.membership.tier.name}
                          </div>
                          <div className="text-gray-500">
                            {formatCurrency(patron.membership.amount)}/
                            {patron.membership.billingCycle === "MONTHLY"
                              ? "month"
                              : "year"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(patron.membership.status)}`}
                        >
                          {getStatusIcon(patron.membership.status)}
                          <span className="ml-1">
                            {patron.membership.status}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">
                            {formatCurrency(patron.totalSpent)}
                          </div>
                          <div className="text-gray-500">
                            Patron since{" "}
                            {formatDate(patron.membership.patronSince)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="flex items-center">
                            <span className="text-gray-900 font-medium">
                              {patron.engagementScore}%
                            </span>
                            <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${patron.engagementScore}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="text-gray-500">
                            {patron.currentStreak}mo streak
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskLevelColor(patron.riskLevel)}`}
                        >
                          {patron.riskLevel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedPatron(patron);
                              setPatronNote(patron.notes);
                              setShowNoteModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Add/Edit Note"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            className="text-purple-600 hover:text-purple-900"
                            title="View Details"
                          >
                            <DocumentTextIcon className="w-4 h-4" />
                          </button>
                          <button
                            className="text-green-600 hover:text-green-900"
                            title="Send Message"
                          >
                            <EnvelopeIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Note Modal */}
      <Modal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        title="Patron Notes"
        size="md"
      >
        <ModalContent>
          {selectedPatron && (
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {selectedPatron.user.firstName.charAt(0)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {selectedPatron.user.firstName}{" "}
                    {selectedPatron.user.lastName}
                  </h3>
                  <p className="text-sm text-gray-600">
                    @{selectedPatron.user.username}
                  </p>
                </div>
              </div>

              {/* Tags section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {patronTags.length === 0 && (
                    <span className="text-sm text-gray-500">No tags assigned</span>
                  )}
                  {patronTags.map((tag) => (
                    <span key={tag.id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border" style={{ borderColor: tag.color || '#e5e7eb' }}>
                      <span className="mr-2" style={{ color: tag.color || '#374151' }}>{tag.name}</span>
                      <button className="text-gray-400 hover:text-gray-600" onClick={() => handleUnassignTag(tag.id)} aria-label={`Remove ${tag.name}`}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <select
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1"
                    value={assignTagId}
                    onChange={(e) => setAssignTagId(e.target.value)}
                  >
                    <option value="">Select a tag to assign</option>
                    {allTags
                      .filter((t) => !patronTags.some((pt) => pt.id === t.id))
                      .map((tag) => (
                        <option key={tag.id} value={tag.id}>
                          {tag.name}
                        </option>
                      ))}
                  </select>
                  <Button variant="outline" onClick={handleAssignTag} disabled={!assignTagId}>
                    Assign
                  </Button>
                </div>
              </div>

              {/* Add note */}
              <Textarea
                label="Add a new note"
                rows={4}
                value={patronNote}
                onChange={(e) => setPatronNote(e.target.value)}
                placeholder="Add notes about this patron's engagement, preferences, or interactions..."
                helperText="Only visible to you and other admins"
              />

              {/* Notes list */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">Notes</h4>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {patronNotes
                    .slice()
                    .sort((a, b) => {
                      if (a.pinned && !b.pinned) return -1;
                      if (!a.pinned && b.pinned) return 1;
                      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                    })
                    .map((note) => (
                      <div key={note.id} className="border border-gray-200 rounded-md p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {editingNoteId === note.id ? (
                              <div className="space-y-2">
                                <Textarea rows={3} value={editingContent} onChange={(e) => setEditingContent(e.target.value)} />
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={saveEditNote}>Save</Button>
                                  <Button size="sm" variant="outline" onClick={cancelEditNote}>Cancel</Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <p className="text-gray-900 whitespace-pre-wrap">{note.content}</p>
                                <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                                  {note.pinned && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">Pinned</span>}
                                  <span>{new Date(note.createdAt).toLocaleString()}</span>
                                </div>
                              </>
                            )}
                          </div>
                          <div className="ml-3 flex flex-col gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleTogglePin(note.id, note.pinned)}>
                              {note.pinned ? 'Unpin' : 'Pin'}
                            </Button>
                            {editingNoteId !== note.id && (
                              <Button size="sm" variant="outline" onClick={() => startEditNote(note.id, note.content)}>Edit</Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => handleDeleteNote(note.id)}>Delete</Button>
                          </div>
                        </div>
                      </div>
                    ))}

                  {patronNotes.length === 0 && (
                    <div className="text-sm text-gray-500">No notes yet. Add your first note above.</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">
                    Total Spent:
                  </span>
                  <p className="text-gray-900">
                    {formatCurrency(selectedPatron.totalSpent)}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Engagement:</span>
                  <p className="text-gray-900">
                    {selectedPatron.engagementScore}%
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    Current Streak:
                  </span>
                  <p className="text-gray-900">
                    {selectedPatron.currentStreak} months
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Risk Level:</span>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(selectedPatron.riskLevel)}`}
                  >
                    {selectedPatron.riskLevel}
                  </span>
                </div>
              </div>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setShowNoteModal(false)}
            disabled={isSavingNote}
          >
            Cancel
          </Button>
          <Button onClick={handleAddNote} loading={isSavingNote}>
            Add Note
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PatronsPage;
